import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { SPIEGA_RUOLO, ruoliAssegnabili, ruoloVecchio } from '../../lib/ruoli';

interface Props {
  onClose: () => void;
  user?: { id: string; email: string; firstName: string; lastName: string; roleId: string; avatarColor: string; active: boolean };
}

const COLORS = ['#050514', '#2550a0', '#a04f25', '#4b2570', '#1f6857', '#705525', '#555555', '#888888'];

export function NewUserModal({ onClose, user }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    email: user?.email ?? '',
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    password: '',
    roleId: user?.roleId ?? '',
    avatarColor: user?.avatarColor ?? COLORS[0]!,
    active: user?.active ?? true,
  });

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<Array<{ id: string; key: string | null; name: string; shortLabel: string; color: string }>>('/roles'),
  });

  const save = useMutation({
    mutationFn: () => user
      ? api.patch(`/users/${user.id}`, {
          email: form.email, firstName: form.firstName, lastName: form.lastName,
          roleId: form.roleId, avatarColor: form.avatarColor, active: form.active,
        })
      : api.post('/users', {
          email: form.email, password: form.password,
          firstName: form.firstName, lastName: form.lastName,
          roleId: form.roleId, avatarColor: form.avatarColor, active: form.active,
        }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      toast.ok(user ? 'Utente aggiornato' : 'Utente creato', `${form.firstName} ${form.lastName}`);
      onClose();
    },
    onError: (err) => toast.alert(user ? 'Errore aggiornamento utente' : 'Errore creazione utente', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const canSubmit = form.email && form.firstName && form.lastName && form.roleId && (user || form.password.length >= 8);
  // Ogni ruolo detto in una frase: prima di dare l'accesso si sa cosa vedrà.
  // I quattro ruoli (lib/ruoli.ts); un ruolo vecchio resta in lista solo per chi lo ha già.
  const scelte = [...ruoliAssegnabili(roles), ...(roles ?? []).filter((r) => r.id === user?.roleId && ruoloVecchio(r.key))];
  const spiegaRuolo = SPIEGA_RUOLO[roles?.find((r) => r.id === form.roleId)?.key ?? ''];

  return (
    <Modal
      title={user ? 'Modifica utente' : 'Nuovo utente'}
      kicker="Impostazioni · Utenti"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={!canSubmit || save.isPending}>
            {user ? 'Salva' : 'Crea utente'} <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-section">
        <div className="form-section-hd">Anagrafica</div>
        <div className="form-grid">
          <Field label="Nome" required span={6}>
            <input className="inp" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
          </Field>
          <Field label="Cognome" required span={6}>
            <input className="inp" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          </Field>
          <Field label="Email" required span={12}>
            <input className="inp" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          {!user && (
            <Field label="Password iniziale" required hint="L'utente potrà cambiarla al primo login" span={12}>
              <input className="inp" type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </Field>
          )}
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-hd">Permessi</div>
        <div className="form-grid">
          <Field label="Ruolo" required span={12}>
            <select className="inp" value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value })}>
              <option value="">— seleziona —</option>
              {scelte.map((r) => <option key={r.id} value={r.id}>{r.name}{ruoloVecchio(r.key) ? ' (ruolo vecchio)' : ''}</option>)}
            </select>
            {spiegaRuolo && <p className="mute text-[12px] mt-1.5">{spiegaRuolo}</p>}
          </Field>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-hd">Aspetto</div>
        <div className="form-grid">
          <Field label="Colore avatar" span={12}>
            <div className="flex gap-1.5 items-center">
              {COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setForm({ ...form, avatarColor: c })}
                  className="w-7 h-7 rounded-full border-2 transition"
                  style={{ background: c, borderColor: form.avatarColor === c ? 'var(--accent)' : 'transparent', transform: form.avatarColor === c ? 'scale(1.15)' : undefined }} />
              ))}
            </div>
          </Field>
          <Field span={12}>
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Utente attivo (può accedere)
            </label>
          </Field>
        </div>
      </div>
    </Modal>
  );
}
